import React from "react";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  BRAND,
  primaryButtonSx,
  inputSx,
  mobileInputSx,
} from "../../../components/auth/authTheme";
import { MobileFormFooter } from "../profileViewHelpers";

const SectionCard = ({ title, icon, isMobile, action, children }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: isMobile ? "14px" : 0,
      border: isMobile ? `1px solid ${BRAND.border}` : "none",
      p: isMobile ? 1.5 : 0,
      mb: isMobile ? 1.5 : 2,
      boxShadow: isMobile ? "0 1px 6px rgba(26, 43, 74, 0.04)" : "none",
    }}
  >
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.75,
          mb: 1.25,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                bgcolor: BRAND.orangeLight,
                color: BRAND.orangeHover,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
          {title && (
            <Typography sx={{ fontWeight: 700, color: BRAND.navy, fontSize: "0.9375rem" }}>
              {title}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

const formatDateForInput = (value) => {
  if (!value) return "";
  const str = String(value);
  return str.includes("T") ? str.split("T")[0] : str.slice(0, 10);
};

const ExperienceDetailEditForm = ({ userDetails, onCancel, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const gridSpacing = isMobile ? 1.5 : 2;

  const initialValues = {
    id: userDetails.id,
    profile: {
      experience: userDetails?.profile_data?.experience?.length
        ? userDetails.profile_data.experience.map((exp) => ({
            ...exp,
            joining: formatDateForInput(exp.joining),
            end: formatDateForInput(exp.end),
          }))
        : [{ company: "", joining: "", end: "", designation: "" }],
    },
  };

  const validationSchema = Yup.object().shape({
    profile: Yup.object().shape({
      experience: Yup.array().of(
        Yup.object().shape({
          company: Yup.string().required("Required"),
          joining: Yup.string().required("Required"),
          end: Yup.string().required("Required"),
          designation: Yup.string().required("Required"),
        })
      ),
    }),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={onSave}
    >
      {({ values, handleChange, handleBlur, errors, touched, isSubmitting }) => (
        <Form>
          <FieldArray name="profile.experience">
            {({ push, remove }) => (
              <Grid container spacing={gridSpacing} sx={{ ml: 0, width: "100%" }}>
                {values.profile.experience.map((exp, index) => (
                  <Grid item xs={12} key={index}>
                    {isMobile ? (
                      <SectionCard
                        isMobile={isMobile}
                        title={`Experience ${index + 1}`}
                        icon={<WorkOutlineIcon sx={{ fontSize: 16 }} />}
                        action={
                          values.profile.experience.length > 1 ? (
                            <IconButton
                              size="small"
                              onClick={() => remove(index)}
                              aria-label="Remove experience"
                              sx={{ color: BRAND.orangeHover }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          ) : null
                        }
                      >
                        <Grid container spacing={gridSpacing}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Company Name"
                              name={`profile.experience[${index}].company`}
                              value={exp.company}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.profile?.experience?.[index]?.company &&
                                Boolean(errors.profile?.experience?.[index]?.company)
                              }
                              helperText={
                                touched.profile?.experience?.[index]?.company &&
                                errors.profile?.experience?.[index]?.company
                              }
                              sx={fieldSx}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Designation"
                              name={`profile.experience[${index}].designation`}
                              value={exp.designation}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.profile?.experience?.[index]?.designation &&
                                Boolean(errors.profile?.experience?.[index]?.designation)
                              }
                              helperText={
                                touched.profile?.experience?.[index]?.designation &&
                                errors.profile?.experience?.[index]?.designation
                              }
                              sx={fieldSx}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              type="date"
                              label="Joining Date"
                              name={`profile.experience[${index}].joining`}
                              value={exp.joining}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              InputLabelProps={{ shrink: true }}
                              error={
                                touched.profile?.experience?.[index]?.joining &&
                                Boolean(errors.profile?.experience?.[index]?.joining)
                              }
                              helperText={
                                touched.profile?.experience?.[index]?.joining &&
                                errors.profile?.experience?.[index]?.joining
                              }
                              sx={fieldSx}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              type="date"
                              label="End Date"
                              name={`profile.experience[${index}].end`}
                              value={exp.end}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              InputLabelProps={{ shrink: true }}
                              error={
                                touched.profile?.experience?.[index]?.end &&
                                Boolean(errors.profile?.experience?.[index]?.end)
                              }
                              helperText={
                                touched.profile?.experience?.[index]?.end &&
                                errors.profile?.experience?.[index]?.end
                              }
                              sx={fieldSx}
                            />
                          </Grid>
                        </Grid>
                      </SectionCard>
                    ) : (
                      <Grid
                        container
                        spacing={gridSpacing}
                        sx={{ mb: 2, borderBottom: "1px solid #ccc", pb: 2 }}
                      >
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Company Name"
                            name={`profile.experience[${index}].company`}
                            value={exp.company}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.profile?.experience?.[index]?.company &&
                              Boolean(errors.profile?.experience?.[index]?.company)
                            }
                            helperText={
                              touched.profile?.experience?.[index]?.company &&
                              errors.profile?.experience?.[index]?.company
                            }
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Designation"
                            name={`profile.experience[${index}].designation`}
                            value={exp.designation}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.profile?.experience?.[index]?.designation &&
                              Boolean(errors.profile?.experience?.[index]?.designation)
                            }
                            helperText={
                              touched.profile?.experience?.[index]?.designation &&
                              errors.profile?.experience?.[index]?.designation
                            }
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="date"
                            label="Joining Date"
                            name={`profile.experience[${index}].joining`}
                            value={exp.joining}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            InputLabelProps={{ shrink: true }}
                            error={
                              touched.profile?.experience?.[index]?.joining &&
                              Boolean(errors.profile?.experience?.[index]?.joining)
                            }
                            helperText={
                              touched.profile?.experience?.[index]?.joining &&
                              errors.profile?.experience?.[index]?.joining
                            }
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="date"
                            label="End Date"
                            name={`profile.experience[${index}].end`}
                            value={exp.end}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            InputLabelProps={{ shrink: true }}
                            error={
                              touched.profile?.experience?.[index]?.end &&
                              Boolean(errors.profile?.experience?.[index]?.end)
                            }
                            helperText={
                              touched.profile?.experience?.[index]?.end &&
                              errors.profile?.experience?.[index]?.end
                            }
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <IconButton
                            onClick={() => remove(index)}
                            disabled={values.profile.experience.length === 1}
                          >
                            <DeleteOutlineIcon color="error" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={() =>
                      push({ company: "", joining: "", end: "", designation: "" })
                    }
                    fullWidth={isMobile}
                    sx={
                      isMobile
                        ? {
                            minHeight: 44,
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: BRAND.border,
                            color: BRAND.navy,
                            borderStyle: "dashed",
                            mb: 0.5,
                          }
                        : { mb: 2 }
                    }
                  >
                    Add Experience
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  {isMobile ? (
                    <MobileFormFooter onCancel={onCancel} isSubmitting={isSubmitting} />
                  ) : (
                    <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                      <Button variant="outlined" onClick={onCancel}>
                        Cancel
                      </Button>
                      <Button variant="contained" type="submit" sx={primaryButtonSx}>
                        Save
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
            )}
          </FieldArray>
        </Form>
      )}
    </Formik>
  );
};

export default ExperienceDetailEditForm;
